import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { BrIconComponent } from '../../../shared/ui/icon/icon.component';
import { DynamicColumn, DynamicFieldType } from './dynamic-framework.models';

export interface LeadsV2FieldDialogData {
  title: string;
  field?: DynamicColumn;
  availableFields?: DynamicColumn[];
  existingKeys: string[];
  keyLocked: boolean;
  typeLocked: boolean;
}

@Component({
  selector: 'app-leads-v2-field-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    BrIconComponent,
  ],
  templateUrl: './leads-v2-field-dialog.component.html',
  styleUrls: ['./leads-v2-field-dialog.component.css'],
})
export class LeadsV2FieldDialogComponent {
  readonly fieldTypes: { value: DynamicFieldType; label: string }[] = [
    { value: 'shortText', label: 'Short text' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'boolean', label: 'Yes / No' },
    { value: 'choice', label: 'Choice' },
  ];

  readonly field: DynamicColumn = this.copyField(this.data.field);
  selectedExistingKey = '';
  error = '';

  get definitionLocked(): boolean {
    return !!this.selectedExistingKey;
  }

  selectExistingField(key: string): void {
    this.selectedExistingKey = key;
    if (!key) {
      Object.assign(this.field, this.copyField());
      return;
    }

    const selected = this.data.availableFields?.find(field => field.key === key);
    if (!selected) return;
    Object.assign(this.field, this.copyField(selected), {
      isRequired: false,
      sortOrder: 0,
    });
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: LeadsV2FieldDialogData,
    private readonly dialogRef: MatDialogRef<LeadsV2FieldDialogComponent, DynamicColumn>,
  ) {}

  onFieldNameChanged(label: string): void {
    const previousGeneratedKey = this.internalKey(this.field.label);
    const shouldUpdateKey = !this.data.keyLocked
      && (!this.field.key || this.field.key === previousGeneratedKey);
    this.field.label = label;
    if (shouldUpdateKey) this.field.key = this.internalKey(label);
  }

  onTypeChanged(): void {
    this.field.defaultValue = null;
    if (this.field.dataType !== 'choice') this.field.options = [];
  }

  addOption(): void {
    this.field.options.push({
      key: '',
      label: '',
      sortOrder: this.field.options.length,
      isActive: true,
    });
  }

  removeOption(index: number): void {
    this.field.options.splice(index, 1);
    this.field.options.forEach((option, optionIndex) => option.sortOrder = optionIndex);
  }

  save(): void {
    this.error = this.validationError() ?? '';
    if (this.error) return;
    this.field.key = this.field.key.trim();
    this.field.label = this.field.label.trim();
    this.field.description = this.field.description?.trim() || null;
    this.field.isActive = true;
    this.field.options.forEach(option => {
      option.key = option.key.trim();
      option.label = option.label.trim();
    });
    this.dialogRef.close(this.copyField(this.field));
  }

  private validationError(): string | null {
    const key = this.field.key.trim();
    if (!key) return 'Field key is required.';
    if (!/^[a-z][a-z0-9_]{0,63}$/.test(key)) {
      return 'Field key must use lowercase letters, numbers and underscores.';
    }
    if (!this.selectedExistingKey && this.data.existingKeys.includes(key)) {
      return 'Field key must be unique.';
    }
    if (!this.field.label.trim()) return 'Field label is required.';
    if (this.field.dataType !== 'choice') return null;
    if (!this.field.options.length) return 'Add at least one choice option.';
    const optionKeys = this.field.options.map(option => option.key.trim());
    if (optionKeys.some(optionKey => !optionKey) ||
        this.field.options.some(option => !option.label.trim())) {
      return 'Every choice option needs a key and label.';
    }
    if (optionKeys.some(optionKey => !/^[a-z][a-z0-9_]{0,63}$/.test(optionKey))) {
      return 'Choice keys must use lowercase letters, numbers and underscores.';
    }
    if (new Set(optionKeys).size !== optionKeys.length) {
      return 'Choice option keys must be unique.';
    }
    return null;
  }

  private copyField(field?: DynamicColumn): DynamicColumn {
    if (!field) {
      return {
        key: '',
        label: '',
        description: null,
        dataType: 'shortText',
        isRequired: false,
        defaultValue: null,
        isActive: true,
        sortOrder: 0,
        options: [],
      };
    }
    return {
      ...field,
      defaultValue: field.defaultValue ? { ...field.defaultValue } : null,
      options: field.options.map(option => ({ ...option })),
    };
  }

  private internalKey(label: string): string {
    return label
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^[^a-z]+/, '')
      .slice(0, 64)
      .replace(/_+$/, '');
  }
}
